// A version's options, in order (A, B, C …): which one is shown, what the
// next one is called, and what a new option copies from the one it's made
// from. Pure — options.ts saves.
import { DEFAULT_MAINTENANCE_YEARS, OPTION_KIND, OPTION_PRICING_COLUMNS, parseScope, serialiseScope, type OptionKind } from '@higgins/m2q-pricing';
import type { DeviceRecord, Fields } from './device-db';

const LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';


/** The option's own choices a copy carries (not its totals, which it works out, nor acceptance). */
const COPIED_FIELDS = OPTION_PRICING_COLUMNS;

const num = (value: unknown): number => (typeof value === 'number' ? value : 0);

export function versionOptions(records: DeviceRecord[], versionId: string): DeviceRecord[] {
  return records.filter((r) => r.table === 'option' && r.parentId === versionId).sort((a, b) => num(a.fields.hig_sortorder) - num(b.fields.hig_sortorder));
}

/** The option asked for, if it's one of these; else the first. */
export const pickOption = (options: DeviceRecord[], wanted: string | null): DeviceRecord | undefined =>
  options.find((o) => o.id === wanted) ?? options[0];

/** "B – Repaint" after A (or "B – 5-year maintenance"): the first letter no option's name starts with. */
export function nextOptionName(options: DeviceRecord[], kindName = OPTION_KIND.repaint as string): string {
  const used = new Set(options.map((o) => String(o.fields.hig_name ?? '').trim().charAt(0).toUpperCase()));
  const letter = [...LETTERS].find((l) => !used.has(l)) ?? String(options.length + 1);
  return `${letter} – ${kindName}`;
}

/**
 * What a copy made as `kind` changes: a maintenance contract gets its years; a repaint drops the maintenance set-up.
 * Either way its scope is stored at the current version from the start, so a build that can't read a maintenance
 * option refuses it rather than pricing it as a repaint (PR 547 review).
 */
function asKind(copied: Fields, kind: OptionKind): Fields {
  if (kind === OPTION_KIND.maintenance) {
    const years = typeof copied.hig_termyears === 'number' ? copied.hig_termyears : DEFAULT_MAINTENANCE_YEARS;
    return { ...copied, hig_kind: kind, hig_termyears: years, hig_scope: serialiseScope(parseScope(copied.hig_scope as string | null)) };
  }
  const { maintenance: _dropped, ...scope } = parseScope(copied.hig_scope as string | null);
  return { ...copied, hig_kind: kind, hig_termyears: null, hig_maintenancetier: null, hig_scope: serialiseScope(scope) };
}

/** A new option's fields: a copy of `from`'s choices made as `kind` (absent = as `from` is), the next name, placed last. */
export function copiedOptionFields(from: DeviceRecord, options: DeviceRecord[], kind?: OptionKind): Fields {
  const copied = Object.fromEntries(COPIED_FIELDS.map((key) => [key, from.fields[key] ?? null]));
  const fields = kind ? asKind(copied, kind) : copied;
  const last = Math.max(-1, ...options.map((o) => num(o.fields.hig_sortorder)));
  const name = fields.hig_kind === OPTION_KIND.maintenance ? `${String(fields.hig_termyears)}-year maintenance` : OPTION_KIND.repaint;
  return { ...fields, hig_name: nextOptionName(options, name), hig_sortorder: last + 1 };
}
